import * as React from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Avatar,
  Backdrop,
  Brand,
  Breadcrumb,
  BreadcrumbItem,
  Bullseye,
  Button,
  Divider,
  Dropdown,
  DropdownGroup,
  DropdownItem,
  DropdownList,
  Flex,
  FlexItem,
  Icon,
  Masthead,
  MastheadBrand,
  MastheadContent,
  MastheadLogo,
  MastheadMain,
  MastheadToggle,
  MenuToggle,
  MenuToggleElement,
  Nav,
  NavExpandable,
  NavItem,
  NavList,
  NotificationBadge,
  Page,
  PageBreadcrumb,
  PageGroup,
  PageSidebar,
  PageSidebarBody,
  PageToggleButton,
  Spinner,
  ToggleGroup,
  ToggleGroupItem,
  Toolbar,
  ToolbarContent,
  ToolbarGroup,
  ToolbarItem
} from '@patternfly/react-core';

// --- FIXED IMPORTS ---
// We now import AppRouteConfig (the union type) and the routes array
import { AppRouteConfig, routes } from '@app/routes';

import logoLight from '@app/bgimages/cas-light.svg';
import logoDark from '@app/bgimages/cas-dark.svg';
// No bell icon here: NotificationBadge supplies its own, and swaps it for a second one carrying an
// exclamation when it is in its attention state. Passing one in pins it and loses that.
import { AngleDoubleLeftIcon, BarsIcon, EllipsisVIcon, MoonIcon, SunIcon } from '@patternfly/react-icons';
import imgAvatar from '@app/bgimages/avatarImg.svg';
import { NotificationProvider } from './NotificationContext';
import { NotificationDrawerPanel } from './NotificationDrawer';
import { NOTIFICATION_SEVERITY } from '@app/model/Enums';
import { useNotificationStore } from '@app/store/notificationStore';
import { ApiHealthBanner } from '@app/components/ApiHealthBanner';
import { DataCompletenessBanner } from '@app/components/DataCompletenessBanner';
import { PageHeaderSlot } from '@app/components/DetailPageHeader';
import { useAuth } from '@app/components/AuthContext';
import { useBreadcrumbLabelStore } from '@app/hooks/useBreadcrumbLabel';
import { useParameters } from '@app/hooks/useParameters';
import { registerLoadingHandlers, useLoading } from './LoadingContext';
// Disabled along with the <SessionTimeoutModal /> below; restore both together.
// import { SessionTimeoutModal } from '@app/components/SessionTimeoutModal';
import { VersionInfo, VersionInfoModal } from '@app/components/VersionInfo';
import { LoginBanner } from '@app/components/LoginBanner';
import { keycloak, logoutWithReason } from '@app/utils/keycloak';
import { applyDensity, isCondensedStored, saveDensityPreference } from '@app/utils/densityPreference';
import { applyTheme, isDarkThemeStored, saveThemePreference } from '@app/utils/themePreference';
import { getDisplayRole, hasAnyRole } from '@app/utils/roles';

interface IAppLayout {
  children: React.ReactNode;
}

const AppLayout: React.FunctionComponent<IAppLayout> = ({ children }) => {
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  // The kebab that stands in for the user dropdown below md. Separate state because both are
  // in the DOM at all times, only one of them shown.
  const [isKebabOpen, setIsKebabOpen] = React.useState(false);

  // Owned here rather than by VersionInfo because two things open the same dialog: the
  // version stamp at the foot of the sidebar and the About item in the user menu.
  const [isAboutOpen, setIsAboutOpen] = React.useState(false);

  // Seeded from the cookie so the toggle and the logo agree with the theme that
  // index.tsx already applied to <html>.
  const [isDarkTheme, setIsDarkTheme] = React.useState(isDarkThemeStored);
  const [isCondensed, setIsCondensed] = React.useState(isCondensedStored);
  const [sidebarOpen, setSidebarOpen] = React.useState(true);

  // Below PatternFly's xl breakpoint (1200px) the sidebar is not a column of the page grid
  // but an off-canvas overlay, and it only slides in while PageSidebar sees isMobile in the
  // page context. Page fills that in from its resize observer, which it starts only when
  // given onPageResize (or isManagedSidebar) - without it every phone and tablet width,
  // "desktop site" included, keeps the nav parked at translateX(-100%) with the hamburger
  // toggling a state nothing reads.
  const [isMobileView, setIsMobileView] = React.useState(false);

  // The bell. Its contents are pushed over a websocket for the life of the session (see
  // NotificationStream), so everything here reads the store rather than fetching anything itself.
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
  const drawerRef = React.useRef<HTMLDivElement>(null);
  const unreadCount = useNotificationStore((state) => state.unreadCount);
  const notifications = useNotificationStore((state) => state.items);
  const arrivalToken = useNotificationStore((state) => state.arrivalToken);

  // PatternFly plays the bell's animation once per render in which this is true, so it is set when
  // a notification arrives and cleared the moment the animation ends.
  const [shouldNotify, setShouldNotify] = React.useState(false);
  React.useEffect(() => {
    if (arrivalToken > 0) setShouldNotify(true);
  }, [arrivalToken]);

  const { initialized, user } = useAuth();

  // Keeps the service's runtime settings loaded for everything below, which can then read one
  // synchronously through the accessors in parameterStore. Here rather than in a provider because
  // this component *is* the authenticated shell — index.tsx renders the landing page instead when
  // there is no session, and /parameters needs one.
  useParameters();

  // First fill of the bell. The notification socket also loads the inbox when it connects, and
  // whichever gets there first wins - the store ignores a second load while one is in flight. Doing
  // it here as well is what keeps the bell working where the websocket cannot connect at all, which
  // a proxy that does not pass upgrades will do to it.
  React.useEffect(() => {
    void useNotificationStore.getState().load();
  }, []);

  const { loading, startLoading, stopLoading } = useLoading();

  const location = useLocation();
  const navigate = useNavigate();
  const pathnames = location.pathname.split("/").filter(x => x);
  const breadcrumbLabels = useBreadcrumbLabelStore(state => state.labels);

  // Back goes one breadcrumb segment up, carrying the same restoreFilters state the
  // non-active crumb links use so a list view comes back with its filters intact.
  // Null at depth <= 1, where the only parent is Home and the crumb already covers it.
  //
  // A page may name a different origin when it navigates, by passing state.backPath - for
  // a class opened from Classes History and then sent to /classes/:oid to be edited, "up"
  // is the history search the user came from, not the Classes list the URL sits under.
  const requestedBackPath = (location.state as any)?.backPath;
  const backPath = typeof requestedBackPath === 'string' && requestedBackPath.startsWith('/')
    ? requestedBackPath
    : (pathnames.length > 1 ? `/${pathnames.slice(0, -1).join('/')}` : null);

  React.useEffect(() => {
    registerLoadingHandlers(startLoading, stopLoading);
  }, [startLoading, stopLoading]);

  // Crossing the breakpoint changes what the sidebar is, so it also changes what open should
  // mean: an overlay covering the page on arriving at a phone width, an empty grid column on
  // going back to desktop. Called on every resize, hence the guard.
  const onPageResize = (_event, { mobileView }: { mobileView: boolean }) => {
    if (mobileView === isMobileView) return;
    setIsMobileView(mobileView);
    setSidebarOpen(!mobileView);
  };

  // On a phone the overlay hides the page it just navigated to, so a tap on a nav item has to
  // dismiss it. On desktop the sidebar is a column and stays put.
  React.useEffect(() => {
    if (isMobileView) setSidebarOpen(false);
  }, [location.pathname, isMobileView]);

  // UI Handlers
  const onDropdownToggle = () => setIsDropdownOpen(!isDropdownOpen);

  /**
   * How loudly the bell asks to be looked at: `attention` for an unread failure, `unread` for
   * anything else waiting, and `read` for a quiet inbox.
   *
   * The attention check can only see the notifications the panel has loaded, so an error sitting
   * further down than the first page shows as a plain unread count until the user pages to it.
   * Deliberate — the alternative is a second count call per severity on every change, to move a
   * bell that is already lit.
   */
  const badgeVariant = React.useMemo(() => {
    if (unreadCount === 0) return 'read';
    const hasUnreadError = notifications.some(
      (notification) => !notification.read && notification.severity === NOTIFICATION_SEVERITY.ERROR
    );
    return hasUnreadError ? 'attention' : 'unread';
  }, [unreadCount, notifications]);

  // Focus moves into the panel when it opens, so it can be read and dismissed from the keyboard
  // rather than only found with a mouse.
  const toggleDrawer = () => {
    setIsDrawerOpen((wasOpen) => {
      if (!wasOpen) setTimeout(() => drawerRef.current?.focus());
      return !wasOpen;
    });
  };

  const logout = () => logoutWithReason('logged-out');

  const accountManagement = () => keycloak.accountManagement();

  // Breadcrumbs Logic
  const dashboardBreadcrumb = (
    <Breadcrumb>
      <BreadcrumbItem>
        <Link to="/">Home</Link>
      </BreadcrumbItem>
      {pathnames.map((segment, index) => {
        const isLast = index === pathnames.length - 1;
        const to = `/${pathnames.slice(0, index + 1).join('/')}`;
        // A page can override its own crumb via useBreadcrumbLabel() — used to show
        // something meaningful (an RHN ID, an offering ID) instead of an internal id.
        // Overrides are shown verbatim; only derived segments get title-cased.
        const crumb = breadcrumbLabels[to]
          ?? (segment ? segment.charAt(0).toUpperCase() + segment.substring(1).toLowerCase() : '');
        return (
          <BreadcrumbItem key={to} isActive={isLast} >
            {isLast ? crumb : <Link state={{ restoreFilters: true }} to={to}>{crumb}</Link>}
          </BreadcrumbItem>
        );
      })}
    </Breadcrumb>
  );

  const toggleCondensed = () => {
    const isNowCondensed = !isCondensed;
    applyDensity(isNowCondensed);
    saveDensityPreference(isNowCondensed);
    setIsCondensed(isNowCondensed);
  };

  // Dropdown Items
  const userFullName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();

  const userDropdownItems = (
    <>
      {/* Which of the two roles the session is being treated as. Sits in the menu because nothing
          else on screen says it: the difference between the two is a section of the navigation
          that is simply absent, and "the Administration menu is missing" is not something a user
          can read as "you are not an administrator" without being told. An item rather than a
          group heading so it lines up with the rest of the menu, disabled because it is something
          to read rather than something to pick. */}
      <DropdownItem key="role" isDisabled>
        <span style={{ color: 'var(--pf-t--global--text--color--subtle)', fontStyle: 'italic' }}>
          Role: {getDisplayRole()}
        </span>
      </DropdownItem>
      <Divider component="li" key="separator-role"/>
      <DropdownItem key="profile" onClick={accountManagement}>My profile</DropdownItem>
      <Divider component="li" key="separator"/>
      {/*<DropdownItem key="user-mgmt" disabled>Users Management</DropdownItem>*/}
      <DropdownItem key="condensed" hasCheckbox isSelected={isCondensed} onClick={toggleCondensed}>
        Condensed view
      </DropdownItem>
      <DropdownItem key="about" onClick={() => setIsAboutOpen(true)}>About</DropdownItem>
      <Divider component="li" key="separator"/>
      <DropdownItem key="logout" onClick={logout}>Logout</DropdownItem>
    </>
  );

  // Same items as the avatar dropdown, which is hidden below md - without this a phone has no
  // route to account management or logout at all. The name moves into a group label because a
  // kebab has nowhere to show it.
  const kebabDropdownItems = userFullName ? (
    <DropdownGroup label={userFullName}>
      <DropdownList>{userDropdownItems}</DropdownList>
    </DropdownGroup>
  ) : (
    <DropdownList>{userDropdownItems}</DropdownList>
  );

  const isGroupActive = (route: AppRouteConfig, currentPath: string): boolean => {
    if (route.path === currentPath) return true;
    if (route.routes) {
      return route.routes.some(child => isGroupActive(child, currentPath));
    }
    return false;
  };

  const renderNavItems = (
    routesToRender: AppRouteConfig[],
    keyPrefix = 'nav',
    // Roles the enclosing group declared, so a route inside it is filtered on them without
    // having to repeat them - the same inheritance flattenRoutes applies to the guard.
    inheritedRoles?: readonly string[]
  ) => {
    return routesToRender.map((route, index) => {
      // Skip detail pages (like /classes/:oid) that don't have a label
      if (!route.label) return null;

      const roles = route.roles ?? inheritedRoles;
      // Nothing this user cannot open is offered. The guard on the route is what actually
      // refuses it; leaving the item out is so nobody is invited to be refused.
      if (!hasAnyRole(roles)) return null;

      const uniqueKey = `${keyPrefix}-${route.label}-${index}`;

      // If the route has children, render an Expandable folder and recurse!
      if (route.routes && route.routes.length > 0) {
        const children = renderNavItems(route.routes, uniqueKey, roles).filter(Boolean);
        // A folder whose every page is somebody else's is not an empty folder, it is no folder
        // at all. Matters for a group that is open to everybody but holds a restricted page.
        if (children.length === 0) return null;

        const active = isGroupActive(route, location.pathname);
        return (
          <NavExpandable
            key={uniqueKey}
            title={route.label}
            isActive={active}
            isExpanded={active}
          >
            {children}
          </NavExpandable>
        );
      }

      // If it's a standard page, render the NavItem
      if (route.path) {
        return (
          <NavItem
            key={uniqueKey}
            isActive={route.path === location.pathname}
          >
            <NavLink to={route.path} end>
              {route.label}
            </NavLink>
          </NavItem>
        );
      }

      return null;
    });
  };

  const Navigation = (
    <Nav id="nav-primary-simple">
      <NavList id="nav-list-simple">
        {renderNavItems(routes)}
      </NavList>
    </Nav>
  );

  const toggleDarkTheme = (_evt, selected) => {
    const isNowDark = !selected === isDarkTheme;
    applyTheme(isNowDark);
    saveThemePreference(isNowDark);
    setIsDarkTheme(isNowDark);
  };

  const headerToolbar = (
    <Toolbar id="toolbar" isFullHeight isStatic>
      <ToolbarContent>
        <ToolbarGroup variant="action-group-plain" align={{ default: "alignEnd" }} gap={{ default: "gapNone", md: "gapMd" }}>
          <ToolbarItem>
            <ToggleGroup aria-label="Dark theme toggle group">
              <ToggleGroupItem 
                aria-label="Switch to light theme" // <-- Add this
                icon={<Icon size="md"><SunIcon /></Icon>} 
                isSelected={!isDarkTheme} 
                onChange={toggleDarkTheme} 
              />
              <ToggleGroupItem 
                aria-label="Switch to dark theme" // <-- Add this
                icon={<Icon size="md"><MoonIcon /></Icon>} 
                isSelected={isDarkTheme} 
                onChange={toggleDarkTheme} 
              />
            </ToggleGroup>
          </ToolbarItem>
          <ToolbarItem>
            <NotificationBadge
              variant={badgeVariant}
              count={unreadCount}
              shouldNotify={shouldNotify}
              onAnimationEnd={() => setShouldNotify(false)}
              isExpanded={isDrawerOpen}
              onClick={toggleDrawer}
              aria-label={unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications'}
            />
          </ToolbarItem>
          <ToolbarItem visibility={{ default: 'visible', md: 'hidden' }}>
            <Dropdown
              isOpen={isKebabOpen}
              onSelect={() => setIsKebabOpen(false)}
              onOpenChange={setIsKebabOpen}
              popperProps={{ position: 'right' }}
              toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                <MenuToggle
                  ref={toggleRef}
                  aria-label={userFullName ? `User menu for ${userFullName}` : 'User menu'}
                  variant="plain"
                  onClick={() => setIsKebabOpen(!isKebabOpen)}
                  isExpanded={isKebabOpen}
                  icon={<EllipsisVIcon />}
                />
              )}
            >
              {kebabDropdownItems}
            </Dropdown>
          </ToolbarItem>
          <ToolbarItem visibility={{ default: 'hidden', md: 'visible' }}>
            <Dropdown
              isOpen={isDropdownOpen}
              onSelect={() => setIsDropdownOpen(false)}
              onOpenChange={setIsDropdownOpen}
              popperProps={{ position: 'right' }}
              toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                <MenuToggle
                  ref={toggleRef}
                  onClick={onDropdownToggle}
                  isFullHeight
                  isExpanded={isDropdownOpen}
                  icon={<Avatar src={imgAvatar} alt="" isBordered />}
                >
                  {userFullName}
                </MenuToggle>
              )}
            >
              <DropdownList>{userDropdownItems}</DropdownList>
            </Dropdown>
          </ToolbarItem>
        </ToolbarGroup>
      </ToolbarContent>
    </Toolbar>
  );

  const Header = (
    <Masthead>
      <MastheadMain>
        <MastheadToggle>
          <PageToggleButton
            aria-label="Global navigation"
            icon={<BarsIcon />}
            isSidebarOpen={sidebarOpen}
            onSidebarToggle={() => setSidebarOpen(!sidebarOpen)}
          />
        </MastheadToggle>
        <MastheadBrand>
          <MastheadLogo>
            <Brand src={isDarkTheme ? logoDark : logoLight} alt="CAS Logo"/>
          </MastheadLogo>
        </MastheadBrand>
      </MastheadMain>
      <MastheadContent>{headerToolbar}</MastheadContent>
    </Masthead>
  );

  const Sidebar = (
    <PageSidebar isSidebarOpen={sidebarOpen}>
      <PageSidebarBody style={{ padding: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ flexGrow: 1, overflowY: 'auto', padding: '16px 0' }}>
          {Navigation}
        </div>
        <div style={{ flexShrink: 0 }}>
          <VersionInfo onClick={() => setIsAboutOpen(true)} />
        </div>
      </PageSidebarBody>
    </PageSidebar>
  );

  if (!initialized) return <Bullseye><Spinner size="xl" /></Bullseye>;

  return (
    <Page
      mainContainerId="primary-app-container"
      masthead={Header}
      onPageResize={onPageResize}
      sidebar={Sidebar}
      notificationDrawer={
        <NotificationDrawerPanel onClose={() => setIsDrawerOpen(false)} drawerRef={drawerRef} />
      }
      isNotificationDrawerExpanded={isDrawerOpen}
    >
      {/* Sits above everything in the content area whenever the backend is unreachable */}
      <ApiHealthBanner />

      {/* And whenever a collection has outgrown the one request its store loads it with */}
      <DataCompletenessBanner />

      {/* Breadcrumb and page title stick as one block rather than two. Sticking them separately
          means giving the title an offset the height of the breadcrumb, and that height is not a
          constant: condensed view, a wrapped trail and a narrow window all change it. A group
          holds them flush together without anyone having to know it. isFilled={false} keeps the
          group at its content's height - it would otherwise grow to fill a short page.
          See DetailPageHeader, which is what fills the slot. */}
      <PageGroup stickyOnBreakpoint={{ default: 'top' }} isFilled={false}>
        <PageBreadcrumb>
          <Flex
            alignItems={{ default: 'alignItemsCenter' }}
            spaceItems={{ default: 'spaceItemsMd' }}
            flexWrap={{ default: 'nowrap' }}
          >
            {backPath && (
              <>
                <FlexItem>
                  <Button
                    variant="link"
                    isInline
                    id="back-button"
                    icon={<AngleDoubleLeftIcon />}
                    onClick={() => navigate(backPath, { state: { restoreFilters: true } })}
                  >
                    Back
                  </Button>
                </FlexItem>
                <FlexItem>
                  <Divider orientation={{ default: 'vertical' }} />
                </FlexItem>
              </>
            )}
            <FlexItem>{dashboardBreadcrumb}</FlexItem>
          </Flex>
        </PageBreadcrumb>
        <PageHeaderSlot />
      </PageGroup>

      {/* <SessionTimeoutModal /> */}

      {/* Greets the user once per release, on the first sign-in after it is deployed. */}
      <LoginBanner />

      <VersionInfoModal
        isOpen={isAboutOpen}
        onClose={() => setIsAboutOpen(false)}
        theme={isDarkTheme ? 'dark' : 'light'}
      />

      {loading && (
        <Backdrop>
          <Bullseye><Spinner size="xl" /></Bullseye>
        </Backdrop>
      )}
      <NotificationProvider>
        {children}
      </NotificationProvider>
    </Page>
  );
};

export { AppLayout };